import { useState } from 'react';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import type { AssetDto } from '@taqon/contracts';
import { hasCapability, useSession } from '../../shared/session/session';
import { StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { RecordLink } from '../finance/detail';
import { useMyAssets } from './AssetDetailPage';
import { AcknowledgeDialog } from './custody';
import { classLabel, useAssetClasses } from './shared';

/**
 * Self-service (`/assets/my`; FRD-AST-030, view part): the assets assigned to the signed-in
 * user's own employee record, with the acknowledgement they owe where one is required
 * (FRD-AST-025). The User ↔ Employee link says which custody is "own"; it grants nothing by itself.
 */
export function MyAssetsPage() {
  const session = useSession();
  const canAcknowledge = hasCapability(session.data, 'assets.custody_assignment:change');
  const classes = useAssetClasses();
  const mine = useMyAssets();
  const [acknowledging, setAcknowledging] = useState<AssetDto | null>(null);

  return (
    <Stack spacing={3}>
      <PageHeader title="My assets" description="The assets currently assigned to you." />
      {mine.isError && <ErrorState message={mine.error.message} />}
      <Section>
        {mine.isPending && <LoadingState label="Loading your assets" />}
        {mine.data === null && (
          <EmptyState
            title="No employee record is linked to your user"
            detail="Ask your administrator to link your user to your employee record."
          />
        )}
        {mine.data?.length === 0 && <EmptyState title="No assets are assigned to you" />}
        {mine.data !== undefined && mine.data !== null && mine.data.length > 0 && (
          <Table size="small" aria-label="My assets">
            <TableHead>
              <TableRow>
                <TableCell>Asset ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Serial number</TableCell>
                <TableCell>Assigned</TableCell>
                <TableCell>Acknowledgement</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mine.data.map((asset) => {
                const pending =
                  asset.acknowledgementRequired === true && asset.acknowledgedAt === null;
                return (
                  <TableRow key={asset.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <RecordLink to={`/assets/${asset.id}`}>{asset.code}</RecordLink>
                    </TableCell>
                    <TableCell>{asset.name}</TableCell>
                    <TableCell>
                      {classLabel(classes.data?.items ?? [], asset.assetClassId)}
                    </TableCell>
                    <TableCell>{asset.serialNumber ?? '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{asset.custodySince ?? '—'}</TableCell>
                    <TableCell>
                      {pending && canAcknowledge ? (
                        <Button
                          size="small"
                          variant="contained"
                          aria-label={`Acknowledge ${asset.code}`}
                          onClick={(event) => {
                            event.currentTarget.focus();
                            setAcknowledging(asset);
                          }}
                        >
                          Acknowledge
                        </Button>
                      ) : pending ? (
                        'Pending'
                      ) : asset.acknowledgementRequired === true ? (
                        'Acknowledged'
                      ) : (
                        'Not required'
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={asset.lifecycleStatus} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Section>
      {acknowledging !== null && (
        <AcknowledgeDialog
          asset={acknowledging}
          onClose={() => {
            setAcknowledging(null);
          }}
        />
      )}
    </Stack>
  );
}
